import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Users,
  Building,
  MapPin,
  TrendingUp,
  Download,
  Filter,
  Mail,

} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface EmployeeReportData {
  summary: {
    totalEmployees: number
    activeEmployees: number
    newHires: number
    turnover: number
  }
  departmentBreakdown: Array<{
    department: string
    count: number
    percentage: number
    manager: string
  }>
  locationDistribution: Array<{
    location: string
    count: number
    percentage: number
  }>
  tenureAnalysis: {
    averageTenure: number
    distribution: Array<{ range: string; count: number; percentage: number }>
  }
  demographicData: {
    genderRatio: { male: number; female: number; other: number }
    ageGroups: Array<{ group: string; count: number; percentage: number }>
  }
  recentHires: Array<{
    employeeNumber: string
    firstName: string
    lastName: string
    department: string
    position: string
    hireDate: string
    email: string
  }>
}

const EmployeeReports: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })
  const [reportView, setReportView] = useState('summary')

  const { data: employeeData, isLoading } = useQuery({
    queryKey: ['employee-reports', dateRange],
    queryFn: async (): Promise<EmployeeReportData> => {
      const response = await api.get('/reports/employee-detailed', {
        params: dateRange
      })
      return response.data.data
    }
  })

  const summaryCards = [
    {
      title: 'Total Employees',
      value: employeeData?.summary.totalEmployees || 0,
      description: 'All employees',
      icon: Users,
      color: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Active',
      value: employeeData?.summary.activeEmployees || 0,
      description: 'Currently employed',
      icon: TrendingUp,
      color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
    },
    {
      title: 'New Hires',
      value: employeeData?.summary.newHires || 0,
      description: 'This period',
      icon: Users,
      color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
    },
    {
      title: 'Turnover',
      value: `${employeeData?.summary.turnover || 0}%`,
      description: 'Annual rate',
      icon: TrendingUp,
      color: 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
    }
  ]

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const response = await api.get(`/reports/employee-detailed/export`, {
        params: { ...dateRange, format },
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `employee-report-${new Date().toISOString().split('T')[0]}.${format}`
      link.click()
      window.URL.revokeObjectURL(url)

      toast.success(`Employee report exported as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Failed to export report')
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Employee Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Comprehensive employee database analysis and insights</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>CSV</span>
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Report View Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {['summary', 'departments', 'locations', 'demographics', 'tenure'].map((view) => (
          <button
            key={view}
            onClick={() => setReportView(view)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              reportView === view
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {view.charAt(0).toUpperCase() + view.slice(1)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {summaryCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{card.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.description}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Report Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Department Breakdown */}
        {reportView === 'departments' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Department Breakdown</h3>
            <div className="space-y-4">
              {employeeData?.departmentBreakdown.map((dept, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Building className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{dept.department}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Manager: {dept.manager}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white">{dept.count} employees</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{dept.percentage}% of total</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Distribution */}
        {reportView === 'locations' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Location Distribution</h3>
            <div className="space-y-4">
              {employeeData?.locationDistribution.map((location, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div className="font-medium text-gray-900 dark:text-white">{location.location}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white">{location.count} employees</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{location.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Demographic Data */}
        {reportView === 'demographics' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Gender Distribution</h3>
              <div className="space-y-4">
                {employeeData?.demographicData.genderRatio && Object.entries(employeeData.demographicData.genderRatio).map(([gender, count]) => (
                  <div key={gender} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{gender}</span>
                    <div className="text-right">
                      <div className="font-bold text-gray-900 dark:text-white">{count}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {((count / employeeData.summary.totalEmployees) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Age Groups</h3>
              <div className="space-y-3">
                {employeeData?.demographicData.ageGroups.map((group, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">{group.group}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{group.count} ({group.percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${group.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Tenure Analysis */}
        {reportView === 'tenure' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Employee Tenure Analysis</h3>
            <div className="space-y-6">
              <div className="text-center p-6 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <div className="text-3xl font-bold text-indigo-900 dark:text-indigo-300">{employeeData?.tenureAnalysis.averageTenure} years</div>
                <div className="text-sm text-indigo-700 dark:text-indigo-400">Average Employee Tenure</div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tenure Distribution</h4>
                <div className="space-y-3">
                  {employeeData?.tenureAnalysis.distribution.map((tenure, index) => (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">{tenure.range}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{tenure.count} employees ({tenure.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full"
                          style={{ width: `${tenure.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Hires */}
        {reportView === 'summary' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Recent Hires</h3>
            <div className="space-y-4">
              {employeeData?.recentHires.slice(0, 5).map((hire, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{hire.firstName} {hire.lastName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{hire.position} - {hire.department}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">{hire.email}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{hire.employeeNumber}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(hire.hireDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {reportView === 'summary' ? 'Employee Growth' :
             reportView === 'departments' ? 'Department Size Trend' :
             reportView === 'locations' ? 'Geographic Distribution' :
             reportView === 'demographics' ? 'Diversity Metrics' : 'Tenure Trends'}
          </h3>
          <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Users className="h-12 w-12 text-gray-400" />
            <span className="ml-2 text-gray-500 dark:text-gray-400">Chart visualization</span>
          </div>
        </div>
      </div>

      {/* Workforce Insights */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
        <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300 mb-3">Workforce Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800 dark:text-indigo-400">
          <div>Workforce growth: {employeeData?.summary.newHires} new hires this period</div>
          <div>{employeeData?.departmentBreakdown[0]?.department} is largest department with {employeeData?.departmentBreakdown[0]?.count} employees</div>
          <div>Average employee tenure: {employeeData?.tenureAnalysis.averageTenure} years</div>
          <div>Gender diversity: Balanced distribution across departments</div>
        </div>
      </div>
    </div>
  )
}

export default EmployeeReports